/**
 * The stream pop-out (Discord's picture-in-picture inside the app; owner's
 * report 2026-11-01: "when you minimize the livestream it won't show it to
 * you anymore"). While you watch a stream and its voice room is not on
 * screen (a text channel open, a phone's list or the room's chat over it),
 * a small glass player keeps it in a corner: a click (or "Back to the
 * stream") brings the room back with that stream in focus; it can mute the
 * stream's sound (a mouse's hover shows its volume), fold to a bar and
 * stop watching. It is dragged, by a mouse or a finger, to any of the four
 * corners, where it snaps (the corner is remembered); a right-click or a
 * long-press opens its menu (Back to the stream, the stream's sound, Stop
 * watching); where the browser has its own picture-in-picture window, a
 * button floats the video there.
 *
 * Traffic: the pop-out shows the video you chose to watch, as the room
 * would (comms-voice still sends it only while it is in `visible`); folded,
 * it asks for no video at all and the sound plays on (rule 1: sound needs
 * `watch` only). The `stream_popout` flag (default on, resolved for you by
 * the server) turns it off: the stream is then shown in its room only.
 */
import type { Pid, Snowflake, VoiceVideoRef } from '@neosocial/protocol';
import { useLayout } from '@neosocial/ui/layout';
import { LONG_PRESS_SLOP } from '@neosocial/ui/touch';
import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent,
  type RefObject,
} from 'react';
import { flushSync } from 'react-dom';
import { tr, useT } from '../i18n';
import { storage } from '../lib/storage';
import { useConfig } from '../store/config';
import { useData } from '../store/data';
import { displayName } from '../store/selectors';
import { goHub } from '../store/ui';
import { gradStyle, Icon } from '../ui/kit';
import { openMenu, rememberAnchor, type MenuItem } from '../ui/overlay';
import { usePanes } from '../shell/panes';
import { setVisibleVideos, streamSoundOf, useLocalMedia } from '../voice/live';
import type { VoiceSession } from '../voice/machine';
import { useVoice } from '../voice/store';
import {
  liveItems,
  stopWatchingStream,
  StreamSoundButton,
  streamSoundItems,
  useScreenStream,
  VideoView,
} from './Streams';
import { askStream, popoutFlagOn, setMiniFolded, useStreamView } from './streamView';

/**
 * The stream the pop-out shows: the screen you started watching last when
 * you still watch it, else another you watch; live, in your channel, never
 * your own. Null when there is none.
 */
export function miniPid(s: VoiceSession, last: Pid | null): Pid | null {
  if (s.status !== 'connected' || !s.channelId) return null;
  const ok = (p: Pid) => {
    const m = s.members[p];
    return !!m && !!m.screen && m.channel === s.channelId && p !== s.pid && !!s.watching[p];
  };
  if (last !== null && ok(last)) return last;
  for (const k of Object.keys(s.watching)) {
    const p = Number(k);
    if (ok(p)) return p;
  }
  return null;
}

/** Is the pop-out up now (the flag on, a stream you watch, its room away)? Its stream's pid. */
export function miniShown(o: {
  session: VoiceSession;
  lastWatch: Pid | null;
  stageOn: string | null;
  flags: Record<string, boolean | undefined> | undefined;
}): Pid | null {
  if (!popoutFlagOn(o.flags)) return null;
  if (o.session.channelId && o.stageOn === o.session.channelId) return null;
  return miniPid(o.session, o.lastWatch);
}

/**
 * What the pop-out has on screen (for `visible`): its stream, unless it is
 * folded. Kept in step synchronously with every store it reads, so the
 * room and the pop-out hand a stream over without a gap (Streams'
 * `useVideoVisibility` lets the stage go before the room's list).
 */
function miniVisible(): VoiceVideoRef[] {
  const pid = miniShown({
    session: useVoice.getState().session,
    lastWatch: useLocalMedia.getState().lastWatch,
    stageOn: useStreamView.getState().stageOn,
    flags: useConfig.getState().config?.flags as Record<string, boolean | undefined> | undefined,
  });
  if (pid === null || useStreamView.getState().folded) return [];
  return [{ pid, source: 'screen' }];
}

let sent = '[]';
let lastSeen: Pid | null = null;
function syncMini(): void {
  // a stream you start watching opens the pop-out again
  const last = useLocalMedia.getState().lastWatch;
  if (last !== lastSeen) {
    lastSeen = last;
    if (last !== null) setMiniFolded(false);
  }
  const next = miniVisible();
  const key = JSON.stringify(next);
  if (key === sent) return;
  sent = key;
  setVisibleVideos(next, 'mini');
}
useVoice.subscribe(syncMini);
useLocalMedia.subscribe(syncMini);
useStreamView.subscribe(syncMini);
useConfig.subscribe(syncMini);

/** A corner of the app the pop-out sits in (logical: start is the left in English). */
export type MiniCorner = 'top-start' | 'top-end' | 'bottom-start' | 'bottom-end';
const CORNERS: readonly string[] = ['top-start', 'top-end', 'bottom-start', 'bottom-end'];
const CORNER_KEY = 'stream-mini-corner';

/** The corner you dragged the pop-out to last, if any (this browser remembers it). */
function savedCorner(): MiniCorner | null {
  const c = storage.get(CORNER_KEY);
  return c && CORNERS.includes(c) ? (c as MiniCorner) : null;
}

/**
 * The corner a dropped pop-out snaps to: the one on the side of its
 * container's middle that the pop-out's own middle is on. `rtl`: start is
 * the right.
 */
export function nearestCorner(
  at: { x: number; y: number },
  box: { left: number; top: number; width: number; height: number },
  rtl: boolean,
): MiniCorner {
  const top = at.y < box.top + box.height / 2;
  const left = at.x < box.left + box.width / 2;
  return `${top ? 'top' : 'bottom'}-${left !== rtl ? 'start' : 'end'}`;
}

/** Can a video float in the browser's own picture-in-picture window here? */
export function pipSupported(): boolean {
  return (
    typeof document !== 'undefined' &&
    !!document.pictureInPictureEnabled &&
    typeof HTMLVideoElement !== 'undefined' &&
    typeof HTMLVideoElement.prototype.requestPictureInPicture === 'function'
  );
}

/**
 * Is the pop-out's own video in the browser's picture-in-picture window
 * now (its button's label, and the frame's "Playing in picture in picture")?
 */
function usePip(frame: RefObject<HTMLElement | null>): boolean {
  const [on, setOn] = useState(false);
  useEffect(() => {
    const read = () => {
      const el = document.pictureInPictureElement;
      setOn(!!el && !!frame.current?.contains(el));
    };
    // fired at the video; read on the way down
    document.addEventListener('enterpictureinpicture', read, true);
    document.addEventListener('leavepictureinpicture', read, true);
    return () => {
      document.removeEventListener('enterpictureinpicture', read, true);
      document.removeEventListener('leavepictureinpicture', read, true);
    };
  }, [frame]);
  return on;
}

const motionOk = () => document.documentElement.dataset.motion !== 'reduce';

/** The pop-out, in the app's corner (Shell mounts it once). */
export function StreamMini() {
  const flags = useConfig(
    (c) => c.config?.flags as Record<string, boolean | undefined> | undefined,
  );
  const lastWatch = useLocalMedia((m) => m.lastWatch);
  const stageOn = useStreamView((v) => v.stageOn);
  const pid = useVoice((v) => miniShown({ session: v.session, lastWatch, stageOn, flags }));
  if (pid === null) return null;
  return <MiniPlayer key={pid} pid={pid} />;
}

/**
 * The pop-out's menu (a right-click, or a long-press by touch: touch.ts
 * sends contextmenu), live while open: Back to the stream, the stream's
 * volume and mute when it has sound, and Stop watching.
 */
function openMiniMenu(
  at: { x: number; y: number },
  frame: HTMLElement,
  o: { userId: Snowflake; audio: boolean; back: () => void; stop: () => void },
): void {
  const { t } = tr();
  rememberAnchor(frame);
  const key = () => {
    const s = streamSoundOf(o.userId);
    return JSON.stringify([s.volume, s.muted]);
  };
  const build = (): MenuItem[] => [
    { id: 'back', label: t('stream.mini.backTip'), icon: 'arrow-in', run: o.back },
    ...(o.audio ? [{ sep: true }, ...streamSoundItems(o.userId)] : []),
    { sep: true },
    { id: 'stop-watching', label: t('stream.stopWatching'), icon: 'cm-eye-off', run: o.stop },
  ];
  openMenu(at, liveItems(key, build), { placement: 'bottom-start', label: t('stream.menu') });
}

function MiniPlayer({ pid }: { pid: Pid }) {
  const { t } = useT();
  const lay = useLayout();
  const member = useVoice((v) => v.session.members[pid]);
  const channelId = useVoice((v) => v.session.channelId);
  const guildId = useVoice((v) => v.session.guildId);
  const userId = member?.user_id ?? '';
  const audio = !!member?.screen_audio;
  const user = useData((d) => d.users[userId]);
  const name = useData((d) =>
    user
      ? displayName(user, guildId ? d.members[guildId]?.[userId] : undefined)
      : (member?.name ?? ''),
  );
  const folded = useStreamView((v) => v.folded);
  const stream = useScreenStream(pid, !folded);
  const label = t('stream.of', { name });
  // the corner: where you dragged it last, else the layout's own (the end
  // side; a phone's under the header, and above the dock on the list).
  // A phone's list keeps a dragged top corner too (QA V-08: it fell back
  // to the bottom)
  const [corner, setCorner] = useState<MiniCorner | null>(savedCorner);
  const list = usePanes((p) => p.pane === 'home');
  const at = corner ?? (lay.layout === 'phone' && !list ? 'top-end' : 'bottom-end');
  const ref = useRef<HTMLElement>(null);
  /** A press on the frame: it becomes a drag once it moves past the long-press's slop. */
  const press = useRef<{ id: number; x: number; y: number; moved: boolean } | null>(null);
  /** The press that ended was a drag: its click must not also go back to the stream. */
  const dragged = useRef(false);
  const pip = usePip(ref);
  const pipOk = pipSupported() && !folded && !!stream;
  const back = () => {
    if (!channelId) return;
    askStream(channelId, userId);
    goHub({ kind: 'voice', channelId });
  };
  const stop = () => stopWatchingStream({ pid, userId });
  const togglePip = () => {
    if (pip) {
      void document.exitPictureInPicture().catch(() => {});
      return;
    }
    void ref.current
      ?.querySelector('video')
      ?.requestPictureInPicture()
      .catch(() => {});
  };
  const onPointerDown = (e: PointerEvent<HTMLElement>) => {
    dragged.current = false;
    press.current = null;
    // the tools keep their own presses (the volume slider drags itself)
    if (e.button > 0 || (e.target as Element).closest('.hubv-mini-tools')) return;
    press.current = { id: e.pointerId, x: e.clientX, y: e.clientY, moved: false };
  };
  const onPointerMove = (e: PointerEvent<HTMLElement>) => {
    const p = press.current;
    if (!p || p.id !== e.pointerId) return;
    const dx = e.clientX - p.x;
    const dy = e.clientY - p.y;
    if (!p.moved) {
      if (Math.hypot(dx, dy) <= LONG_PRESS_SLOP) return;
      p.moved = true;
      e.currentTarget.setPointerCapture?.(e.pointerId);
      e.currentTarget.dataset.dragging = '';
    }
    e.currentTarget.style.transform = `translate(${dx}px, ${dy}px)`;
  };
  // dropped: it snaps to the nearest corner, gliding there from where it was let go
  const onPointerEnd = (e: PointerEvent<HTMLElement>) => {
    const p = press.current;
    if (!p || p.id !== e.pointerId) return;
    press.current = null;
    if (!p.moved) return;
    // the click this release makes (if any) comes in this same task
    dragged.current = true;
    setTimeout(() => (dragged.current = false), 0);
    const el = e.currentTarget;
    const from = el.getBoundingClientRect();
    const box = (el.offsetParent ?? document.documentElement).getBoundingClientRect();
    const rtl = getComputedStyle(el).direction === 'rtl';
    const next = nearestCorner(
      { x: from.left + from.width / 2, y: from.top + from.height / 2 },
      box,
      rtl,
    );
    storage.set(CORNER_KEY, next);
    el.style.transform = '';
    delete el.dataset.dragging;
    flushSync(() => setCorner(next));
    const to = el.getBoundingClientRect();
    if (motionOk())
      el.animate?.(
        [
          { transform: `translate(${from.left - to.left}px, ${from.top - to.top}px)` },
          { transform: 'none' },
        ],
        { duration: 220, easing: 'cubic-bezier(0.2, 0, 0, 1)' },
      );
  };
  const tools = (
    <span className="hubv-mini-tools">
      {audio ? (
        // the folded bar has no room under it for the volume (the menu has it)
        <StreamSoundButton userId={userId} slider={!folded} data={{ 'data-mini': 'sound' }} />
      ) : null}
      {pipOk ? (
        <button
          type="button"
          className="hubv-tt"
          aria-label={t(pip ? 'stream.mini.pipExit' : 'stream.mini.pip')}
          data-tip={t(pip ? 'stream.mini.pipExit' : 'stream.mini.pip')}
          data-mini="pip"
          onClick={togglePip}
        >
          <Icon name="pip" size="m" />
        </button>
      ) : null}
      <button
        type="button"
        className="hubv-tt"
        aria-label={t(folded ? 'stream.mini.show' : 'stream.mini.fold')}
        data-tip={t(folded ? 'stream.mini.show' : 'stream.mini.fold')}
        data-mini={folded ? 'show' : 'fold'}
        onClick={() => setMiniFolded(!folded)}
      >
        <Icon name={folded ? 'win-max' : 'win-min'} size="m" />
      </button>
      <button
        type="button"
        className="hubv-tt"
        aria-label={t('stream.stopWatching')}
        data-tip={t('stream.stopWatching')}
        data-mini="stop"
        onClick={stop}
      >
        <Icon name="x" size="m" />
      </button>
    </span>
  );
  const head = (
    <span className="hubv-mini-head">
      <span className="nx-live-badge" role="img" aria-label={t('stream.liveLabel')}>
        {t('stream.live')}
      </span>
      <span className="hubv-mini-name">
        <bdi dir="auto">{name}</bdi>
      </span>
    </span>
  );
  return (
    <section
      ref={ref}
      className={'hubv-mini' + (folded ? ' folded' : '')}
      data-layout={lay.layout}
      data-corner={at}
      data-pip={pip ? '' : undefined}
      data-user={userId}
      aria-label={label}
      style={
        { ...gradStyle(userId), '--tools': 2 + (audio ? 1 : 0) + (pipOk ? 1 : 0) } as CSSProperties
      }
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerEnd}
      onPointerCancel={onPointerEnd}
      onClickCapture={(e) => {
        if (!dragged.current) return;
        dragged.current = false;
        e.preventDefault();
        e.stopPropagation();
      }}
      onContextMenu={(e) => {
        e.preventDefault();
        // a long-press opened it: that finger no longer drags
        press.current = null;
        if ((e.target as Element).closest('input')) return;
        openMiniMenu({ x: e.clientX, y: e.clientY }, e.currentTarget, {
          userId,
          audio,
          back,
          stop,
        });
      }}
    >
      {folded ? (
        <>
          {/* the bar: no video comes; a click shows it again */}
          <button
            type="button"
            className="hubv-mini-bar"
            aria-label={t('stream.mini.show')}
            onClick={() => setMiniFolded(false)}
          >
            {head}
          </button>
          {tools}
        </>
      ) : (
        <>
          <button
            type="button"
            className="hubv-mini-go"
            aria-label={t('stream.mini.back', { name })}
            data-tip={t('stream.mini.backTip')}
            onClick={back}
          >
            {stream ? (
              <>
                <VideoView stream={stream} fit="contain" label={label} />
                {/* it plays in the browser's own window: the frame says so,
                    not a black box with the browser's placeholder (QA V-07) */}
                {pip ? (
                  <span className="hubv-tile-wait hubv-mini-pipnote" role="status">
                    <Icon name="pip" size="s" />
                    {t('stream.mini.pipOn')}
                  </span>
                ) : null}
              </>
            ) : (
              <span className="hubv-tile-wait" role="status">
                <i className="nx-spin" aria-hidden="true" />
                {t('stream.loading')}
              </span>
            )}
          </button>
          {head}
          {tools}
        </>
      )}
    </section>
  );
}
